import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

import {
  FaComments,
  FaEye,
  FaWallet,
  FaHeart,
  FaSlidersH,
  FaTimes,
  FaUsers,
  FaPlayCircle,
  FaTrash,
  FaBroadcastTower,
  FaPhoneAlt
} from 'react-icons/fa';

import {
  useLocation,
  useNavigate
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import UserModal from '../components/UserModal';
import '../css/Navbar.css';

import fallbackProfile from '../assets/couple.png';

function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const stompClientRef = useRef(null);

  const [notificationCounts, setNotificationCounts] = useState({
    likes: 0,
    messages: 0,
    views: 0,
    missedCalls: 0
  });

  const [currentUser, setCurrentUser] = useState(null);
  const [profileImage, setProfileImage] = useState(
    '/default-profile.png'
  );

  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const [likedByUsers, setLikedByUsers] = useState([]);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [likesLoading, setLikesLoading] = useState(false);

  const [missedCalls, setMissedCalls] = useState([]);
  const [showMissedCallsModal, setShowMissedCallsModal] = useState(false);
  const [missedCallsLoading, setMissedCallsLoading] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const [navbarError, setNavbarError] = useState('');

  const token = localStorage.getItem('authToken');

  const authorizationHeader = useMemo(() => {
    if (!token) return null;

    return token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
  }, [token]);

  const isSuggestionsPage =
    location.pathname === '/suggestions';

  const currentLocale =
    i18n.language?.startsWith('fr')
      ? 'fr-FR'
      : 'en-US';

  const loadCurrentUser = useCallback(() => {
    try {
      const userJson =
        localStorage.getItem('currentUser');

      const user = userJson
        ? JSON.parse(userJson)
        : null;

      setCurrentUser(user);

      setProfileImage(
        user?.mediaUrls?.[0] ||
        '/default-profile.png'
      );
    } catch (error) {
      console.error(
        '❌ Unable to read current user:',
        error
      );

      setCurrentUser(null);
      setProfileImage('/default-profile.png');
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!authorizationHeader) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/notifications`,
        {
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Notifications request failed: ${response.status}`
        );
      }

      const data = await response.json();

      const lastSeen =
        localStorage.getItem('likesSeenAt');

      let likes = Number(data.newLikes) || 0;

      if (lastSeen && data.lastLikeAt) {
        const seenDate = new Date(lastSeen);
        const lastLikeDate =
          new Date(data.lastLikeAt);

        if (
          !Number.isNaN(seenDate.getTime()) &&
          !Number.isNaN(lastLikeDate.getTime()) &&
          lastLikeDate <= seenDate
        ) {
          likes = 0;
        }
      }

      setNotificationCounts((previous) => ({
        ...previous,
        likes,
        messages:
          Number(data.newMessages) || 0,
        views:
          Number(data.newViews) || 0
      }));
    } catch (error) {
      console.warn(
        '⚠️ Notifications error:',
        error
      );
    }
  }, [authorizationHeader]);

  const fetchMissedCallCount = useCallback(async () => {
    if (!authorizationHeader) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/calls/missed/count`,
        {
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Missed calls count request failed: ${response.status}`
        );
      }

      const data = await response.json();

      setNotificationCounts((previous) => ({
        ...previous,
        missedCalls: Number(data.count) || 0
      }));
    } catch (error) {
      console.warn(
        '⚠️ Missed calls count error:',
        error
      );
    }
  }, [authorizationHeader]);

  const fetchMissedCalls = async () => {
    if (
      !authorizationHeader ||
      missedCallsLoading
    ) {
      return;
    }

    setMissedCallsLoading(true);
    setNavbarError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/calls/missed`,
        {
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Missed calls request failed: ${response.status}`
        );
      }

      const data = await response.json();

      const calls = Array.isArray(data)
        ? [...data].sort(
          (first, second) =>
            new Date(second.startedAt) -
            new Date(first.startedAt)
        )
        : [];

      setMissedCalls(calls);
      setShowProfileMenu(false);
      setShowLikesModal(false);
      setShowMissedCallsModal(true);

      const unseenCalls = calls.filter(
        (call) => !call.seenByReceiver
      );

      await Promise.allSettled(
        unseenCalls.map((call) =>
          fetch(
            `${process.env.REACT_APP_API_URL}/api/calls/${call.id}/seen`,
            {
              method: 'PATCH',
              headers: {
                Authorization: authorizationHeader
              }
            }
          )
        )
      );

      await fetchMissedCallCount();
    } catch (error) {
      console.error(
        '❌ Failed to fetch missed calls:',
        error
      );

      setNavbarError(
        t(
          'navbar.missedCallsLoadError',
          'Impossible de charger les appels manqués.'
        )
      );
    } finally {
      setMissedCallsLoading(false);
    }
  };

  const openSuggestionsFilters = () => {
    window.dispatchEvent(
      new CustomEvent(
        'open-suggestions-filters'
      )
    );
  };

  const handleNavigation = (path) => {
    setShowProfileMenu(false);
    setShowLikesModal(false);
    setShowMissedCallsModal(false);
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      if (authorizationHeader) {
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/auth/logout`,
          {
            method: 'POST',
            headers: {
              Authorization: authorizationHeader
            }
          }
        );
      }
    } catch (error) {
      console.warn(
        '⚠️ Logout request failed:',
        error
      );
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('selectedChatUser');

      navigate('/', {
        replace: true
      });
    }
  };

  const handleProfileViewsClick = async () => {
    if (!authorizationHeader) return;

    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/profile-views/mark-seen`,
        {
          method: 'POST',
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      setNotificationCounts((previous) => ({
        ...previous,
        views: 0
      }));
    } catch (error) {
      console.warn(
        '⚠️ Could not mark profile views as seen:',
        error
      );
    } finally {
      navigate('/profile-views');
    }
  };

  const fetchLikedByUsers = async () => {
    if (!authorizationHeader || likesLoading) {
      return;
    }

    setLikesLoading(true);
    setNavbarError('');

    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/likes/mark-seen`,
        {
          method: 'POST',
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      localStorage.setItem(
        'likesSeenAt',
        new Date().toISOString()
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/likes`,
        {
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Likes request failed: ${response.status}`
        );
      }

      const data = await response.json();

      setLikedByUsers(
        Array.isArray(data)
          ? [...data].sort(
            (first, second) =>
              new Date(second.createdAt) -
              new Date(first.createdAt)
          )
          : []
      );

      setNotificationCounts((previous) => ({
        ...previous,
        likes: 0
      }));

      setShowProfileMenu(false);
      setShowMissedCallsModal(false);
      setShowLikesModal(true);

      await fetchNotifications();
    } catch (error) {
      console.error(
        '❌ Failed to fetch likes:',
        error
      );

      setNavbarError(
        t(
          'navbar.likesLoadError',
          'Impossible de charger les personnes qui vous ont aimé.'
        )
      );
    } finally {
      setLikesLoading(false);
    }
  };

  const openUserModal = async (userId) => {
    if (!authorizationHeader) return;

    setNavbarError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}/details`,
        {
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `User details request failed: ${response.status}`
        );
      }

      const data = await response.json();

      setSelectedUser(data);
      setShowLikesModal(false);
      setShowMissedCallsModal(false);
      setShowUserModal(true);
    } catch (error) {
      console.error(
        '❌ Impossible d’ouvrir le profil:',
        error
      );

      setNavbarError(
        t(
          'navbar.profileOpenError',
          'Impossible d’ouvrir ce profil.'
        )
      );
    }
  };

  const handleDeleteMissedCall = async (
    event,
    callId
  ) => {
    event.stopPropagation();

    if (!authorizationHeader) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/calls/${callId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Delete missed call failed: ${response.status}`
        );
      }

      setMissedCalls((previousCalls) => {
        const remainingCalls =
          previousCalls.filter(
            (call) => call.id !== callId
          );

        setNotificationCounts((previous) => ({
          ...previous,
          missedCalls:
            remainingCalls.filter(
              (call) => !call.seenByReceiver
            ).length
        }));

        return remainingCalls;
      });

    } catch (error) {
      console.error(
        '❌ Unable to delete missed call:',
        error
      );

      setNavbarError(
        t(
          'navbar.deleteMissedCallError',
          'Impossible de supprimer cet appel.'
        )
      );
    }
  };

  const handleLovelyFromModal = async () => {
    if (
      !selectedUser ||
      !authorizationHeader
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/lovely`,
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Authorization:
              authorizationHeader
          },
          body: JSON.stringify({
            receiverId: selectedUser.id
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          `Lovely request failed: ${response.status}`
        );
      }
    } catch (error) {
      console.error(
        '❌ Like failed:',
        error
      );

      setNavbarError(
        t(
          'navbar.likeError',
          'Impossible d’aimer ce profil pour le moment.'
        )
      );
    }
  };

  const handleChatFromModal = () => {
    if (!selectedUser) return;

    localStorage.setItem(
      'selectedChatUser',
      JSON.stringify(selectedUser)
    );

    setShowUserModal(false);
    navigate('/chat');
  };

  const formatLikeDate = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(
      currentLocale,
      {
        dateStyle: 'short',
        timeStyle: 'short'
      }
    ).format(date);
  };

  useEffect(() => {
    loadCurrentUser();

    const refreshProfileHandler = () => {
      loadCurrentUser();
    };

    window.addEventListener(
      'refresh-profile',
      refreshProfileHandler
    );

    return () => {
      window.removeEventListener(
        'refresh-profile',
        refreshProfileHandler
      );
    };
  }, [loadCurrentUser]);

  useEffect(() => {
    if (!authorizationHeader) return;

    const refreshAllNotifications = () => {
      fetchNotifications();
      fetchMissedCallCount();
    };

    refreshAllNotifications();

    const interval = window.setInterval(
      refreshAllNotifications,
      15000
    );

    window.addEventListener(
      'refresh-notifications',
      refreshAllNotifications
    );

    return () => {
      window.clearInterval(interval);

      window.removeEventListener(
        'refresh-notifications',
        refreshAllNotifications
      );
    };
  }, [
    authorizationHeader,
    fetchNotifications,
    fetchMissedCallCount
  ]);

  useEffect(() => {
    if (
      !authorizationHeader ||
      !currentUser?.id
    ) {
      return undefined;
    }

    const socket = new SockJS(
      `${process.env.REACT_APP_API_URL}/ws?token=${encodeURIComponent(
        token
      )}`
    );

    const client = new Client({
      webSocketFactory: () => socket,
      connectHeaders: {
        Authorization:
          authorizationHeader
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      onConnect: () => {
        client.publish({
          destination: '/app/presence',
          body: JSON.stringify({
            userId: currentUser.id,
            status: 'online'
          })
        });

        client.subscribe(
          '/topic/presence',
          () => { }
        );
      },

      onStompError: (frame) => {
        console.error(
          '❌ Navbar STOMP error:',
          frame.headers?.message
        );
      }
    });

    stompClientRef.current = client;
    client.activate();

    return () => {
      if (client.connected) {
        client.publish({
          destination: '/app/presence',
          body: JSON.stringify({
            userId: currentUser.id,
            status: 'offline'
          })
        });
      }

      client.deactivate();
      stompClientRef.current = null;
    };
  }, [
    authorizationHeader,
    currentUser?.id,
    token
  ]);

  useEffect(() => {
    setShowProfileMenu(false);
    setShowLikesModal(false);
    setShowMissedCallsModal(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="app-navbar">
        <button
          type="button"
          className="app-navbar-profile"
          onClick={() => {
            setShowLikesModal(false);
            setShowMissedCallsModal(false);
            setShowProfileMenu(true);
          }}
          aria-label={t(
            'navbar.openProfileMenu',
            'Ouvrir le menu du profil'
          )}
        >
          <img
            src={profileImage}
            alt={
              currentUser?.name ||
              t('navbar.profile', 'Profil')
            }
            onError={(event) => {
              event.currentTarget.src =
                fallbackProfile;
            }}
          />
        </button>

        <button
          type="button"
          className="app-navbar-icon"
          onClick={fetchLikedByUsers}
          aria-label={t(
            'navbar.likes',
            'Personnes qui vous aiment'
          )}
        >
          <FaHeart />

          {notificationCounts.likes > 0 && (
            <span className="app-navbar-badge">
              {notificationCounts.likes > 9
                ? '9+'
                : notificationCounts.likes}
            </span>
          )}
        </button>

        <button
          type="button"
          className="app-navbar-icon"
          onClick={() => navigate('/chat')}
          aria-label={t(
            'navbar.messages',
            'Messages'
          )}
        >
          <FaComments />

          {notificationCounts.messages > 0 && (
            <span className="app-navbar-badge">
              {notificationCounts.messages > 9
                ? '9+'
                : notificationCounts.messages}
            </span>
          )}
        </button>

        <button
          type="button"
          className="app-navbar-icon"
          onClick={handleProfileViewsClick}
          aria-label={t(
            'navbar.profileViews',
            'Visites du profil'
          )}
        >
          <FaEye />

          {notificationCounts.views > 0 && (
            <span className="app-navbar-badge">
              {notificationCounts.views > 9
                ? '9+'
                : notificationCounts.views}
            </span>
          )}
        </button>

        <button
          type="button"
          className="app-navbar-icon"
          onClick={fetchMissedCalls}
          aria-label={t(
            'navbar.missedCalls',
            'Appels manqués'
          )}
        >
          <FaPhoneAlt />

          {notificationCounts.missedCalls > 0 && (
            <span className="app-navbar-badge">
              {notificationCounts.missedCalls > 9
                ? '9+'
                : notificationCounts.missedCalls}
            </span>
          )}
        </button>

        <button
          type="button"
          className={`app-navbar-live-button ${location.pathname === '/live' ||
            location.pathname.startsWith('/live/')
            ? 'active'
            : ''
            }`}
          onClick={() => navigate('/live')}
          aria-label={t(
            'live.activeLives',
            'Live Now'
          )}
          title={t(
            'live.activeLives',
            'Live Now'
          )}
        >
          <span className="app-navbar-live-dot" />

          <FaBroadcastTower />
        </button>
        <button
          type="button"
          className={
            location.pathname.startsWith('/moments')
              ? 'app-navbar-moment-button active'
              : 'app-navbar-moment-button'
          }
          onClick={() => navigate('/moments')}
          aria-label="Moments"
        >
          <FaPlayCircle />
        </button>

        {isSuggestionsPage && (
          <button
            type="button"
            className="app-navbar-icon app-navbar-filter"
            onClick={openSuggestionsFilters}
            aria-label={t(
              'filters.title',
              'Filtres'
            )}
          >
            <FaSlidersH />
          </button>
        )}

        <button
          type="button"
          className="app-navbar-icon app-navbar-suggestions"
          onClick={() =>
            navigate('/suggestions')
          }
          aria-label={t(
            'navbar.suggestions',
            'Suggestions'
          )}
        >
          <FaUsers />
        </button>
      </nav>

      {!location.pathname.startsWith('/live') &&
        !location.pathname.startsWith('/chat') &&
        !location.pathname.startsWith('/suggestions') &&
        !location.pathname.startsWith('/views') &&
        !location.pathname.startsWith('/likes') && (

          <button
            type="button"
            className="app-go-live-floating"
            onClick={() => navigate('/live/start')}
          >
            <span className="app-go-live-pulse" />

            <FaBroadcastTower />

            <span>
              {t('live.goLive', 'Go Live')}
            </span>
          </button>
        )}

      {navbarError && (
        <div
          className="app-navbar-toast"
          role="alert"
        >
          {navbarError}

          <button
            type="button"
            onClick={() =>
              setNavbarError('')
            }
          >
            <FaTimes />
          </button>
        </div>
      )}

      {showProfileMenu && (
        <div
          className="app-profile-backdrop"
          onMouseDown={() =>
            setShowProfileMenu(false)
          }
          role="presentation"
        >
          <section
            className="app-profile-menu"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-menu-title"
          >
            <button
              type="button"
              className="app-profile-close"
              onClick={() =>
                setShowProfileMenu(false)
              }
              aria-label={t(
                'common.close',
                'Fermer'
              )}
            >
              <FaTimes />
            </button>

            <div className="app-profile-summary">
              <img
                src={profileImage}
                alt={
                  currentUser?.name ||
                  t(
                    'navbar.profile',
                    'Profil'
                  )
                }
                onError={(event) => {
                  event.currentTarget.src =
                    fallbackProfile;
                }}
              />

              <h2 id="profile-menu-title">
                {currentUser?.name ||
                  t(
                    'navbar.user',
                    'Utilisateur'
                  )}
              </h2>

              <p>
                ID: {currentUser?.id || '—'}
              </p>
            </div>

            <div className="app-profile-links">
              <button
                type="button"
                onClick={() =>
                  handleNavigation(
                    '/mon-profil'
                  )
                }
              >
                <FaUsers />
                {t(
                  'navbar.myProfile',
                  'Mon profil'
                )}
              </button>

              <button
                type="button"
                onClick={() =>
                  handleNavigation('/earnings')
                }
              >
                <FaWallet />

                {t(
                  'navbar.myEarnings',
                  'My Earnings'
                )}
              </button>

              <button
                type="button"
                onClick={() =>
                  handleNavigation('/terms')
                }
              >
                {t(
                  'navbar.terms',
                  'Conditions'
                )}
              </button>

              <button
                type="button"
                className="app-profile-link-disabled"
                disabled
              >
                {t(
                  'navbar.settings',
                  'Paramètres'
                )}
              </button>

              <button
                type="button"
                className="app-profile-link-disabled"
                disabled
              >
                {t(
                  'navbar.helpCenter',
                  'Centre d’aide'
                )}
              </button>

              <button
                type="button"
                className="app-profile-logout"
                onClick={handleLogout}
              >
                {t(
                  'navbar.logout',
                  'Déconnexion'
                )}
              </button>
            </div>
          </section>
        </div>
      )}

      {showLikesModal && (
        <div
          className="app-likes-backdrop"
          onMouseDown={() =>
            setShowLikesModal(false)
          }
          role="presentation"
        >
          <section
            className="app-likes-modal"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
            role="dialog"
            aria-modal="true"
            aria-labelledby="likes-modal-title"
          >
            <header className="app-likes-header">
              <div>
                <span>
                  <FaHeart />
                </span>

                <div>
                  <h2 id="likes-modal-title">
                    {t(
                      'navbar.likedYou',
                      'Ils vous aiment'
                    )}
                  </h2>

                  <p>
                    {t(
                      'navbar.likedYouSubtitle',
                      'Découvrez les personnes intéressées par votre profil.'
                    )}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="app-likes-close"
                onClick={() =>
                  setShowLikesModal(false)
                }
                aria-label={t(
                  'common.close',
                  'Fermer'
                )}
              >
                <FaTimes />
              </button>
            </header>

            <div className="app-likes-content">
              {likesLoading ? (
                <div className="app-likes-state">
                  <div className="app-likes-spinner" />

                  <p>
                    {t(
                      'common.loading',
                      'Chargement...'
                    )}
                  </p>
                </div>
              ) : likedByUsers.length === 0 ? (
                <div className="app-likes-empty">
                  <span>
                    <FaHeart />
                  </span>

                  <h3>
                    {t(
                      'navbar.noLikesTitle',
                      'Aucun nouveau like'
                    )}
                  </h3>

                  <p>
                    {t(
                      'navbar.noLikes',
                      'Les personnes qui aiment votre profil apparaîtront ici.'
                    )}
                  </p>
                </div>
              ) : (
                <div className="app-likes-list">
                  {likedByUsers.map((user) => (
                    <button
                      key={`${user.id}-${user.createdAt || ''}`}
                      type="button"
                      className={`app-liked-user ${user.isNew
                        ? 'is-new'
                        : ''
                        }`}
                      onClick={() =>
                        openUserModal(user.id)
                      }
                    >
                      <div className="app-liked-avatar-wrapper">
                        <img
                          src={
                            user.mediaUrls?.[0] ||
                            '/default-profile.png'
                          }
                          alt={
                            user.name ||
                            t(
                              'navbar.user',
                              'Utilisateur'
                            )
                          }
                          onError={(event) => {
                            event.currentTarget.src =
                              fallbackProfile;
                          }}
                        />

                        {user.isNew && (
                          <span className="app-liked-new-dot" />
                        )}
                      </div>

                      <div className="app-liked-information">
                        <h3>
                          {user.name ||
                            t(
                              'navbar.user',
                              'Utilisateur'
                            )}

                          {user.age && (
                            <span>
                              , {user.age}
                            </span>
                          )}
                        </h3>

                        <p>
                          {user.gender ||
                            t(
                              'user.notSpecified',
                              'Non précisé'
                            )}
                        </p>

                        {user.createdAt && (
                          <small>
                            {formatLikeDate(
                              user.createdAt
                            )}
                          </small>
                        )}
                      </div>

                      <span className="app-liked-open">
                        {t(
                          'navbar.viewProfile',
                          'Voir'
                        )}
                      </span>
                    </button>


                  ))}
                </div>


              )}
            </div>


          </section>
        </div>
      )}

      {showMissedCallsModal && (
        <div
          className="app-likes-backdrop"
          onMouseDown={() =>
            setShowMissedCallsModal(false)
          }
          role="presentation"
        >
          <section
            className="app-likes-modal"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
            role="dialog"
            aria-modal="true"
            aria-labelledby="missed-calls-modal-title"
          >
            <header className="app-likes-header">
              <div>
                <span>
                  <FaPhoneAlt />
                </span>

                <div>
                  <h2 id="missed-calls-modal-title">
                    {t(
                      'navbar.missedCalls',
                      'Appels manqués'
                    )}
                  </h2>

                  <p>
                    {t(
                      'navbar.missedCallsSubtitle',
                      'Consultez les appels aux quels vous n’avez pas répondu.'
                    )}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="app-likes-close"
                onClick={() =>
                  setShowMissedCallsModal(false)
                }
                aria-label={t(
                  'common.close',
                  'Fermer'
                )}
              >
                <FaTimes />
              </button>
            </header>

            <div className="app-likes-content">
              {missedCallsLoading ? (
                <div className="app-likes-state">
                  <div className="app-likes-spinner" />

                  <p>
                    {t(
                      'common.loading',
                      'Chargement...'
                    )}
                  </p>
                </div>
              ) : missedCalls.length === 0 ? (
                <div className="app-likes-empty">
                  <span>
                    <FaPhoneAlt />
                  </span>

                  <h3>
                    {t(
                      'navbar.noMissedCallsTitle',
                      'Aucun appel manqué'
                    )}
                  </h3>

                  <p>
                    {t(
                      'navbar.noMissedCalls',
                      'Vos appels manqués apparaîtront ici.'
                    )}
                  </p>
                </div>
              ) : (
                <div className="app-likes-list">
                  {missedCalls.map((call) => (
                    <div
                      key={call.id}
                      className={`app-liked-user app-missed-item ${!call.seenByReceiver ? 'is-new' : ''
                        }`}
                    >
                      <div className="app-liked-avatar-wrapper">
                        <img
                          src={fallbackProfile}
                          alt={
                            call.callerName ||
                            t('navbar.user', 'User')
                          }
                        />

                        {!call.seenByReceiver && (
                          <span className="app-liked-new-dot" />
                        )}
                      </div>

                      <div className="app-liked-information">
                        <h3>
                          {call.callerName ||
                            t('navbar.user', 'User')}
                        </h3>

                        <p>
                          {String(call.callType).toUpperCase() ===
                            'VIDEO'
                            ? t(
                              'navbar.missedVideoCall',
                              'Missed video call'
                            )
                            : t(
                              'navbar.missedAudioCall',
                              'Missed audio call'
                            )}
                        </p>

                        {call.startedAt && (
                          <small>
                            {formatLikeDate(call.startedAt)}
                          </small>
                        )}
                      </div>

                      <div className="app-missed-actions">
                        <button
                          type="button"
                          className="app-missed-open"
                          onClick={() =>
                            openUserModal(call.callerId)
                          }
                        >
                          {t('navbar.viewProfile', 'View')}
                        </button>

                        <button
                          type="button"
                          className="app-missed-delete"
                          onClick={(event) =>
                            handleDeleteMissedCall(
                              event,
                              call.id
                            )
                          }
                          aria-label={t(
                            'navbar.deleteMissedCall',
                            'Delete missed call'
                          )}
                          title={t(
                            'navbar.deleteMissedCall',
                            'Delete missed call'
                          )}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>


              )}


            </div>
          </section>
        </div>
      )}

      <UserModal
        show={showUserModal}
        handleClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onLovely={handleLovelyFromModal}
        onChat={handleChatFromModal}
      />
    </>
  );
}

export default Navbar;